/* Authenticated application shell: sidebar rail, top bar, mobile tab bar, overlays. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useData } from "../data";
import { useSession, homeFor } from "./session";
import { useCountry } from "../lib/country";
import { navFor, navItemFor, TABBAR, type NavItem } from "./nav";
import { useOverlays } from "./overlays";
import {
  useAllEmployees, useApprovals, useExpenses, useExpiringDocuments, useLeaves, useLetters, useNotifications,
  useMarkNotificationsRead, useOffboarding, useUnmatchedUsers,
} from "../data/hooks";
import { Avatar, Icon, IconButton, Modal, Button, cx, useToast, errMsg } from "../ui";
import type { Role } from "../data/types";
import HelpDrawer from "../pages/help/HelpDrawer";
import PageGuide from "../pages/help/PageGuide";
import Tour from "../pages/help/Tour";
import AssistantDrawer from "../pages/ai/AssistantDrawer";
import SubBanner from "../pages/billing/SubBanner";

const ROLES: [Role, string, string][] = [["owner", "Owner", "المالك"], ["hr", "HR Officer", "مسؤول الموارد البشرية"], ["accountant", "Accountant", "المحاسب"], ["employee", "Employee", "الموظف"], ["admin", "StartHR admin", "إدارة ستارت HR"]];

function useBadges(role: Role | null) {
  const tenant = role === "owner" || role === "hr" || role === "accountant";
  const ohr = role === "owner" || role === "hr";
  const approvals = useApprovals({ enabled: tenant });
  const emps = useAllEmployees({ enabled: tenant });
  const leaves = useLeaves({ enabled: ohr });
  const expenses = useExpenses({ enabled: tenant });
  const letters = useLetters({ enabled: ohr });
  const offb = useOffboarding({ enabled: ohr });
  const docs = useExpiringDocuments(30, { enabled: ohr });
  const unmatched = useUnmatchedUsers({ enabled: ohr });
  const n = (x: number | undefined) => (x ? x : null);
  return {
    approvals: n(approvals.data?.length),
    employees: n(emps.data?.filter((e) => e.status !== "Terminated").length),
    leave: n(leaves.data?.filter((l) => l.status === "Pending").length),
    expenses: n(expenses.data?.filter((x) => x.status === "Pending").length),
    letters: n(letters.data?.filter((x) => x.status === "Pending").length),
    offboarding: n(offb.data?.filter((o) => o.status !== "Settled").length),
    docs30: n(docs.data?.length),
    unmatched: n(unmatched.data?.length),
  } as Record<NonNullable<NavItem["badge"]>, number | null>;
}

/** When the signed-in user switches the language, remember it on the account (PATCH /me) so their emails use it too. */
function useRememberLang(lang: "en" | "ar", signedIn: boolean) {
  const p = useData();
  const first = useRef(lang);
  useEffect(() => {
    if (!signedIn || lang === first.current) return;
    first.current = lang;
    p.updateMe({ lang }).catch(() => { /* best effort: the UI language already changed */ });
  }, [lang, signedIn, p]);
}

export default function Shell({ children }: { children: ReactNode }) {
  const i = useI18n();
  const { t, L } = i;
  const { user, role, switchRole, logout } = useSession();
  useRememberLang(i.lang, !!user);
  const ov = useOverlays();
  const loc = useLocation();
  const nav = useNavigate();
  const toast = useToast();
  const badges = useBadges(role);
  const country = useCountry();
  const items = navFor(role, country);
  const cur = navItemFor(loc.pathname, country);
  const notifs = useNotifications({ enabled: !!role && role !== "admin" });
  const markRead = useMarkNotificationsRead();
  const unread = (notifs.data || []).some((n) => !n.read);

  useEffect(() => { ov.setNavOpen(false); window.scrollTo?.(0, 0); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ov.open({ kind: "cmd" }); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "j" && role !== "admin") { e.preventDefault(); ov.open({ kind: "assistant" }); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ov, role]);

  useEffect(() => {
    const parts = crumbs(loc.pathname, cur, i.L);
    document.title = (parts[parts.length - 1] || "") + " · " + L("StartHR", "ستارت HR");
  }, [loc.pathname, cur, i.L, L]);

  const doSwitch = async (r: Role) => {
    try { await switchRole(r); ov.close(); nav(homeFor(r)); toast(L("Viewing as ", "العرض باسم ") + L(ROLES.find((x) => x[0] === r)![1], ROLES.find((x) => x[0] === r)![2])); }
    catch (e) { toast(errMsg(e), "bad"); }
  };
  const doLogout = async () => { await logout(); nav("/login"); };

  let sec: string | null = null;
  const tab = TABBAR[role === "employee" ? "employee" : role === "admin" ? "admin" : role === "accountant" ? "accountant" : "default"];
  const parts = crumbs(loc.pathname, cur, i.L);

  return (
    <div className={cx("shell", ov.navOpen && "nav-open", ov.collapsed && "collapsed")}>
      <aside className="rail" aria-label={L("Main navigation", "القائمة الرئيسية")}>
        <div className="brand"><span className="brand-mark">{L("S", "س")}</span><span className="brand-name">{L("StartHR", "ستارت HR")}<small>{role === "admin" ? L("Platform admin", "إدارة المنصة") : t("Al Waha Trading Est.")}</small></span></div>
        <nav className="nav" data-testid="sidebar">
          {items.map((n) => {
            const head = n.section && n.section[0] !== sec ? <div className="nav-sec">{L(n.section[0], n.section[1])}</div> : null;
            if (n.section) sec = n.section[0];
            const c = n.badge ? badges[n.badge] : null;
            const exact = n.path === "/ess" || n.path === "/admin";
            return (
              <div key={n.path} style={{ display: "contents" }}>
                {head}
                <NavLink end={exact} to={n.path} className={({ isActive }) => cx("nav-item", (isActive || cur?.path === n.path) && "on")} title={L(n.label[0], n.label[1])} data-testid={"nav-" + n.path.slice(1).replace(/\//g, "-")}>
                  <Icon name={n.icon} /><span className="lbl">{L(n.label[0], n.label[1])}</span>
                  {c != null && <span className={cx("nav-count", n.alert && "alert")}>{c}</span>}
                </NavLink>
              </div>
            );
          })}
        </nav>
        <div className="rail-foot">
          <div className="ksa"><Icon name="shield" /><span>{t("Data hosted in KSA")} · PDPL</span></div>
          <button type="button" className="nav-item" onClick={doLogout} title={t("Sign out")} data-testid="logout"><Icon name="log-out" /><span className="lbl">{t("Sign out")}</span></button>
        </div>
      </aside>
      <div className="scrim" onClick={() => ov.setNavOpen(false)} />
      <div className="main">
        <header className="top">
          <IconButton icon={ov.collapsed ? "panel" : "menu"} label={t("Menu")} data-testid="menu-toggle" onClick={() => (window.innerWidth >= 1024 ? ov.setCollapsed(!ov.collapsed) : ov.setNavOpen(!ov.navOpen))} />
          <div className="crumbs" data-testid="crumbs">
            {parts.map((p, k) => k === parts.length - 1 ? <b key={k}>{p}</b> : <span key={k} style={{ display: "contents" }}><span>{p}</span><Icon name="chevron-right" className="s" /></span>)}
          </div>
          <button type="button" className="searchbtn" onClick={() => ov.open({ kind: "cmd" })} data-testid="cmd-open"><Icon name="search" /><span>{t("Search employees, pages, documents…")}</span><span className="kbd">Ctrl K</span></button>
          <div className="top-end">
            <label className="role" title={t("View as")}>
              <span className="role-chip">{t("View as")}</span>
              <select className="sel" value={role ?? ""} aria-label={t("View as")} data-testid="role-select" onChange={(e) => doSwitch(e.target.value as Role)}>
                {ROLES.map((r) => <option key={r[0]} value={r[0]}>{L(r[1], r[2])}</option>)}
              </select>
            </label>
            {role !== "admin" && <IconButton className="aitop" icon="sparkle" label={L("AI assistant (Ctrl+J)", "المساعد الذكي (Ctrl+J)")} onClick={() => ov.open({ kind: "assistant" })} data-testid="assistant-open" />}
            <IconButton label={L("Help", "المساعدة")} onClick={() => ov.open({ kind: "help" })} data-testid="help-open">?</IconButton>
            <IconButton className="langbtn" label={i.isAr ? "Switch to English" : t("Switch to Arabic")} onClick={i.toggleLang} data-testid="lang-toggle">{L("ع", "EN")}</IconButton>
            <IconButton icon={i.isDark ? "sun" : "moon"} label={i.isDark ? t("Light mode") : t("Dark mode")} onClick={i.toggleTheme} data-testid="theme-toggle" />
            {role !== "admin" && <IconButton icon="bell" label={t("Notifications")} onClick={() => ov.open({ kind: "notifs" })} data-testid="notifs-open">{unread && <span className="dot" />}</IconButton>}
            <IconButton label={user ? i.L(user.name, user.nameAr) : ""} onClick={() => ov.open({ kind: "role" })} data-testid="user-menu"><Avatar name={user?.name || "?"} /></IconButton>
          </div>
        </header>
        <main className="content" id="page" data-testid="page">
          {role !== "admin" && role !== "employee" && <SubBanner />}
          <PageGuide path={loc.pathname} />
          {children}
        </main>
      </div>
      <nav className="tabbar" aria-label={L("Quick navigation", "تنقل سريع")} data-testid="tabbar">
        {tab.map(([p, l, ic]) => <Link key={p} to={p} className={loc.pathname === p || (p !== "/ess" && p !== "/admin" && loc.pathname.startsWith(p + "/")) ? "on" : ""}><Icon name={ic} /><span>{L(l[0], l[1])}</span></Link>)}
        {role !== "employee" && <button type="button" onClick={() => ov.setNavOpen(true)} data-testid="tab-more"><Icon name="menu" /><span>{t("More")}</span></button>}
      </nav>

      {ov.overlay?.kind === "help" && <HelpDrawer topic={ov.overlay.topic} onClose={ov.close} />}
      {ov.overlay?.kind === "assistant" && <AssistantDrawer onClose={ov.close} />}
      {ov.overlay?.kind === "tour" && <Tour onClose={ov.close} />}
      {ov.overlay?.kind === "cmd" && <CommandPalette onClose={ov.close} items={items} />}
      {ov.overlay?.kind === "notifs" && (
        <Modal title={t("Notifications")} className="drawer" onClose={ov.close} testId="notifs" foot={<Button onClick={() => { markRead.mutate(undefined); ov.close(); }}>{L("Mark all as read", "تعليم الكل كمقروء")}</Button>}>
          <div className="stack">
            {(notifs.data || []).length === 0 && <p className="muted">{L("You're all caught up.", "لا توجد إشعارات جديدة.")}</p>}
            {(notifs.data || []).map((n) => (
              <Link key={n.id} to={n.route} className={cx("alert", n.tone || "info", "tint")} onClick={ov.close}>
                <span className="ai"><Icon name="bell" /></span><div className="grow"><div className="alert-t">{n.title}</div><div className="alert-d">{n.body}</div></div>
              </Link>
            ))}
          </div>
        </Modal>
      )}
      {ov.overlay?.kind === "role" && (
        <Modal title={user ? L(user.name, user.nameAr) : ""} onClose={ov.close} testId="role-sheet">
          <div className="stack">
            <p className="sm muted">{user?.email}</p>
            <div className="prs-h">{L("Switch demo role", "تبديل دور العرض")}</div>
            {ROLES.map((r) => <Button key={r[0]} variant={r[0] === role ? "pri" : undefined} onClick={() => doSwitch(r[0])} data-testid={"role-" + r[0]}>{L(r[1], r[2])}</Button>)}
            <Button icon="play" onClick={() => ov.open({ kind: "tour" })}>{L("Take the product tour", "ابدأ الجولة التعريفية")}</Button>
            <Button icon="log-out" variant="ghost" onClick={doLogout}>{t("Sign out")}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function crumbs(path: string, cur: NavItem | undefined, L: (en: string, ar: string) => string): string[] {
  const out: string[] = [];
  if (path.startsWith("/admin")) return [L("Platform admin", "إدارة المنصة"), cur ? L(cur.label[0], cur.label[1]) : ""];
  if (cur?.section) out.push(L(cur.section[0], cur.section[1]));
  if (cur) out.push(L(cur.label[0], cur.label[1]));
  if (/^\/employees\/new/.test(path)) out.push(L("Add employee", "إضافة موظف"));
  else if (/^\/employees\/\d+/.test(path)) out.push(L("Profile", "الملف"));
  if (path === "/payroll/run") out.push(L("Payroll run", "مسير الرواتب"));
  if (path.startsWith("/payslip/")) { out.splice(0, out.length, L("Pay", "الرواتب"), L("Payroll", "الرواتب"), L("Payslip", "قسيمة الراتب")); }
  if (path.startsWith("/biometric/devices/")) out.push(L("Device", "الجهاز"));
  if (path === "/biometric/unmatched") out.push(L("Unmatched users", "مستخدمون غير مرتبطين"));
  if (path === "/biometric/punches") out.push(L("Punch log", "سجل البصمات"));
  if (path === "/gov/integrations") out.push(L("Integrations", "التكاملات"));
  return out.filter(Boolean);
}

function CommandPalette({ onClose, items }: { onClose: () => void; items: NavItem[] }) {
  const { L, t, name } = useI18n();
  const { role } = useSession();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const emps = useAllEmployees({ enabled: role === "owner" || role === "hr" || role === "accountant" });
  const res = useMemo(() => {
    const s = q.trim().toLowerCase();
    const pages = items.filter((n) => !s || (n.label[0] + " " + n.label[1]).toLowerCase().includes(s)).slice(0, 8).map((n) => ({ to: n.path, label: L(n.label[0], n.label[1]), icon: n.icon, meta: L("Page", "صفحة") }));
    const people = s ? (emps.data || []).filter((e) => (e.nameEn + " " + e.nameAr + " " + e.code + " " + e.idNumber).toLowerCase().includes(s)).slice(0, 8).map((e) => ({ to: "/employees/" + e.id, label: name(e), icon: "user", meta: e.code })) : [];
    return [...people, ...pages];
  }, [q, items, emps.data, L, name]);
  const go = (to: string) => { onClose(); nav(to); };
  return (
    <Modal title={t("Search")} className="cmd" onClose={onClose} testId="cmd">
      <input className="inp" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search employees, pages, documents…")} aria-label={t("Search")} data-testid="cmd-input" onKeyDown={(e) => { if (e.key === "Enter" && res[0]) go(res[0].to); }} />
      <div className="stack" style={{ marginTop: 12, gap: 4 }}>
        {res.length === 0 && <p className="muted sm">{L("No results", "لا توجد نتائج")}</p>}
        {res.map((r) => <button key={r.to} type="button" className="nav-item" onClick={() => go(r.to)} data-testid="cmd-result"><Icon name={r.icon} /><span className="lbl">{r.label}</span><span className="xs muted" style={{ marginInlineStart: "auto" }}>{r.meta}</span></button>)}
      </div>
    </Modal>
  );
}
